import { getConfig } from "./config/environment";

const config = getConfig();

// SEO Configuration e utilità
export const seoConfig = {
	siteName: 'Auting',
	// TODO: replace with the officially registered legal entity name (e.g. "Auting S.r.l.")
	legalName: 'Yago S.r.l.',
	siteUrl: config.site.url,
	// On-site logo used in Organization schema (absolute URL is built where consumed)
	logo: '/favicon.png',
	defaultDescription: {
		en: 'Choose the perfect car for you, book it and drive. Thousands of private cars are waiting for you all over Italy. Forget car rental, forget car sharing, discover Auting\'s peer to peer car sharing!',
		it: 'Scegli l\'auto perfetta per te, prenotala e parti. Migliaia di auto di privati ti aspettano in tutta Italia. Dimentica il noleggio auto, dimentica il car sharing, scopri il car sharing tra privati di Auting!',
		fr: 'Devenez Owner d\'Auting et gagnez de l\'argent en partageant votre voiture. Enregistrez votre voiture gratuitement en quelques étapes simples. Partagez-la quand vous voulez, au prix que vous souhaitez.'
	},
	defaultKeywords: {
		en: 'car sharing, car mobility, peer to peer car rental, Auting, rent car, rent a car, rent a car from private owner, private car rental Italy, discover cars, car hire Italy, ok mobility, travel Italy by car, insured car rental, Reale Mutua insurance, affordable car rental, car sharing platform',
		it: 'car sharing, noleggio auto tra privati, Auting, noleggio auto peer to peer, affitto auto da privati, car sharing privati, auto in affitto, mobilità, viaggiare in Italia, Reale Mutua, assicurazione auto, noleggio auto economico, piattaforma car sharing',
		fr: 'car sharing, autopartage Italie, location de voiture entre particuliers, Auting, rent car, rent a car, louer voiture particulier, location de voiture, voiture avec assurance, voyager en Italie, Reale Mutua assurance, plateforme de car sharing, location voiture pas chère'
	},
	author: 'Auting',
	twitterHandle: '@auting_it',
	linkedinProfile: 'https://linkedin.com/company/auting',
	// Authoritative profiles & press that strengthen Auting's entity graph for AI
	// answer engines. Only non-empty values are emitted into `sameAs` — fill the
	// TODO slots with real URLs and they go live automatically.
	social: {
		linkedin: 'https://linkedin.com/company/auting',
		twitter: 'https://twitter.com/auting_it',
		instagram: '', // TODO: add official Instagram profile URL
		facebook: '', // TODO: add official Facebook page URL
		youtube: ''   // TODO: add official YouTube channel URL (if any)
	},
	press: [
		// TODO: add authoritative press/article URLs (e.g. Wired, Millionaire) — these
		// boost trust signals for LLM-based answer engines.
		// 'https://www.wired.it/...',
		// 'https://www.millionaire.it/...'
	],
	// Site-wide trust rating sourced from Trustpilot. `profileUrl` is also emitted in
	// `sameAs`.
	//
	// ⚠️ The values below are a MANUAL SNAPSHOT and drift over time (especially the
	// review count). Do not treat them as authoritative. The correct fix is to fetch
	// them dynamically from Trustpilot — see docs/TRUSTPILOT_RATING_GUIDE.md
	// (Option B: Business API) which shows how to replace these static values.
	// lastVerified: 2026-06-23
	trustpilot: {
		profileUrl: 'https://it.trustpilot.com/review/www.auting.it',
		ratingValue: 4.8,   // TrustScore ("Excellent") as shown on Trustpilot
		reviewCount: 715    // Snapshot only — automate via the Business API (see guide)
	},
	socialImage: '/images/og-default.jpg',
	foundedYear: '2014',
	businessType: 'Technology Company - Car Sharing between People',
	businessDescription: {
		en: 'Auting is the first Italian peer-to-peer car sharing platform. The service allows you to book a car from a private owner for one or more consecutive days. Travel anywhere in Italy, with the peace of mind of a strong insurance partner like Reale Mutua.',
		it: 'Auting è la prima piattaforma italiana di car sharing peer-to-peer. Prenota un\'auto da un privato per uno o più giorni e viaggia in tutta Italia con la sicurezza di Reale Mutua.',
		fr: 'Auting est la première plateforme italienne de partage de voiture entre particuliers. Louez une voiture pour un ou plusieurs jours et voyagez partout en Italie avec la tranquillité d\'une couverture complète de Reale Mutua.'
	}
};

// Genera meta tags per una pagina specifica
export function generateMetaTags(page, lang = 'en', customMeta = {}) {
	const baseUrl = seoConfig.siteUrl;
	const langPath = lang === 'en' ? '' : `/${lang}`;
	const url = `${baseUrl}${langPath}${page === 'home' ? '' : `/${page}`}`;
	
	const defaults = {
		title: `${seoConfig.siteName} - ${seoConfig.defaultDescription[lang]}`,
		description: seoConfig.defaultDescription[lang],
		keywords: seoConfig.defaultKeywords[lang],
		image: `${baseUrl}${seoConfig.socialImage}`,
		url: url,
		type: 'website'
	};
	
	return { ...defaults, ...customMeta };
}

// Genera hreflang links per SEO multilingua
export function generateHreflangLinks(page = '') {
	const baseUrl = seoConfig.siteUrl;
	const languages = ['en', 'it', 'fr'];
	
	return languages.map(lang => {
		const href = lang === 'en' 
			? `${baseUrl}${page ? `/${page}` : ''}`
			: `${baseUrl}/${lang}${page ? `/${page}` : ''}`;
		
		return {
			rel: 'alternate',
			hreflang: lang,
			href: href
		};
	}).concat([{
		rel: 'alternate',
		hreflang: 'x-default',
		href: `${baseUrl}${page ? `/${page}` : ''}`
	}]);
}

// Builds a list of authoritative profile URLs (sameAs) from configured socials and
// press, dropping any empty/TODO slots so nothing fake is ever emitted.
export function buildSameAs() {
	return [
		...Object.values(seoConfig.social || {}),
		seoConfig.trustpilot?.profileUrl,
		...(seoConfig.press || [])
	].filter(Boolean);
}

// Site-wide AggregateRating for the Organization/Service entities, sourced from
// Trustpilot. Returns null if rating data is missing so callers omit the property.
// TODO (optional): swap the static seoConfig.trustpilot values for a live fetch from
// the Trustpilot Business API to keep the score and count always up to date.
export function getSiteAggregateRating() {
	return buildAggregateRating(seoConfig.trustpilot);
}

// Builds a schema.org AggregateRating object from API data. Returns null when no real
// rating data is available, so callers can omit the property entirely (emitting
// AggregateRating without backing reviews can trigger search/AI penalties).
export function buildAggregateRating(ratings) {
	if (!ratings || !ratings.ratingValue || !ratings.reviewCount) return null;
	return {
		'@type': 'AggregateRating',
		ratingValue: String(ratings.ratingValue),
		reviewCount: String(ratings.reviewCount),
		bestRating: String(ratings.bestRating ?? 5),
		worstRating: String(ratings.worstRating ?? 1)
	};
}

export function generateStructuredData(type, data = {}, lang = 'en') {
	const baseStructure = {
		'@context': 'https://schema.org',
		'@type': type
	};

	switch (type) {
		case 'Organization': {
			const orgRating = getSiteAggregateRating();
			return {
				...baseStructure,
				name: seoConfig.siteName,
				legalName: seoConfig.legalName,
				url: seoConfig.siteUrl,
				logo: `${seoConfig.siteUrl}${seoConfig.logo}`,
				image: `${seoConfig.siteUrl}${seoConfig.logo}`,
				description: seoConfig.businessDescription[lang],
				foundingDate: seoConfig.foundedYear,
				sameAs: buildSameAs(),
				...(orgRating ? { aggregateRating: orgRating } : {}),
				contactPoint: {
					'@type': 'ContactPoint',
					contactType: 'Customer Support',
					email: 'support@auting.it',
				},
				...data
			};
		}
			
		case 'WebSite':
			return {
				...baseStructure,
				name: seoConfig.siteName,
				url: seoConfig.siteUrl,
				description: seoConfig.defaultDescription[lang],
				inLanguage: lang,
				potentialAction: {
					'@type': 'SearchAction',
					target: `${seoConfig.siteUrl}/search?q={search_term_string}`,
					'query-input': 'required name=search_term_string'
				},
				...data
			};
			
		case 'WebPage':
			return {
				...baseStructure,
				name: data.title,
				description: data.description,
				url: data.url,
				inLanguage: lang,
				isPartOf: {
					'@type': 'WebSite',
					name: seoConfig.siteName,
					url: seoConfig.siteUrl
				},
				datePublished: data.datePublished || new Date().toISOString(),
				dateModified: data.dateModified || new Date().toISOString(),
				author: {
					'@type': 'Organization',
					name: seoConfig.siteName
				},
				...data
			};
			
		case 'BreadcrumbList':
			return {
				...baseStructure,
				itemListElement: data.items.map((item, index) => ({
					'@type': 'ListItem',
					position: index + 1,
					name: item.name,
					item: item.url
				}))
			};

		// Peer-to-peer car sharing offering. `aggregateRating` is included only when
		// real rating data is passed in (see buildAggregateRating). Pass a custom
		// `name`/`description`/`url` via `data` for category pages (drivers/owners).
		case 'Service':
		case 'Product': {
			const { aggregateRating, ...rest } = data;
			const offering = {
				...baseStructure,
				name: data.name || seoConfig.siteName,
				description: data.description || seoConfig.businessDescription[lang],
				url: data.url || seoConfig.siteUrl,
				brand: {
					'@type': 'Brand',
					name: seoConfig.siteName
				},
				provider: {
					'@type': 'Organization',
					name: seoConfig.siteName,
					url: seoConfig.siteUrl
				},
				areaServed: {
					'@type': 'Country',
					name: 'Italy'
				},
				...rest
			};
			if (type === 'Service') {
				offering.serviceType = data.serviceType || 'Peer-to-peer car sharing';
			}
			if (aggregateRating) {
				offering.aggregateRating = aggregateRating;
			}
			return offering;
		}

		// FAQ rich result. `data.questions` is an array of { question, answer } strings.
		case 'FAQPage':
			return {
				...baseStructure,
				mainEntity: (data.questions || []).map((qa) => ({
					'@type': 'Question',
					name: qa.question,
					acceptedAnswer: {
						'@type': 'Answer',
						// Strip markdown-ish newlines/bullets into a clean plain-text answer
						text: String(qa.answer || '')
							.replace(/\s*\n\s*/g, ' ')
							.replace(/\s+-\s+/g, ' ')
							.trim()
					}
				}))
			};

		default:
			return { ...baseStructure, ...data };
	}
}

export function generateBreadcrumbs(page, lang = 'en') {
	const baseUrl = seoConfig.siteUrl;
	const langPath = lang === 'en' ? '' : `/${lang}`;
	
	const breadcrumbs = [
		{
			name: lang === 'fr' ? 'Accueil' : lang === 'it' ? 'Home' : 'Home',
			url: `${baseUrl}${langPath}`
		}
	];
	
	if (page && page !== 'home') {
		const pageNames = {
			about: {
				en: 'About Us',
				it: 'Chi Siamo', 
				fr: 'À Propos'
			},
			contact: {
				en: 'Contact',
				it: 'Contatti',
				fr: 'Contact'
			}
		};
		
		breadcrumbs.push({
			name: pageNames[page]?.[lang] || page,
			url: `${baseUrl}${langPath}/${page}`
		});
	}
	
	return breadcrumbs;
}